import React, { useState, useEffect } from "react";
import Loader from "../../Global/Loader";
import axios from "axios";
import Select from "react-select";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { getDisplayValue } from "../../../utils/displayValue";

const FormWithCards = ({ cards, setCards }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    country: "UK",
    city: "",
    programme: "",
    university: "",
    major: "",
  });

  // const [cards, setCards] = useState([]);

  // State for dynamic options
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [majors, setMajors] = useState([]);

  // State to track focus for each field
  const [isFocused, setIsFocused] = useState({
    country: false,
    programme: false,
    city: false,
    university: false,
    major: false,
  });

  // Fetch initial countries on component mount
  useEffect(() => {
    fetchFilterData();
  }, []);

  // Fetch filter data from API
  const fetchFilterData = async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/calculator/filter${queryString ? `?${queryString}` : ""}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!params.country) {
        // Initial load - set countries only
        setCountries(response.data.data.countries || []);
      } else if (!params.city) {
        // Country and program selected - set cities
        setCities(response.data.data.cities || []);
      } else if (!params.universityName) {
        // City selected - set universities
        setUniversities(response.data.data.universities || []);
      } else {
        // University selected - set majors
        setMajors(response.data.data.majors || []);
      }
    } catch (error) {
      // console.error("Error fetching filter data:", error);
    }
  };

  // Handle form field changes
  const handleChange = (name, selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setFormData((prev) => ({ ...prev, [name]: value }));

    switch (name) {
      case "country":
        // Reset dependent fields
        setFormData((prev) => ({
          ...prev,
          programme: "",
          city: "",
          university: "",
          major: "",
        }));
        setCities([]);
        setUniversities([]);
        setMajors([]);
        if (value) {
          fetchFilterData({ country: value });
        }
        break;

      case "programme":
        setFormData((prev) => ({
          ...prev,
          city: "",
          university: "",
          major: "",
        }));
        if (value && formData.country) {
          fetchFilterData({
            country: formData.country,
            program: value,
          });
        }
        break;

      case "city":
        setFormData((prev) => ({
          ...prev,
          university: "",
          major: "",
        }));
        if (value) {
          fetchFilterData({
            country: formData.country,
            program: formData.programme,
            city: value,
          });
        }
        break;

      case "university":
        setFormData((prev) => ({
          ...prev,
          major: "",
        }));
        if (value) {
          fetchFilterData({
            country: formData.country,
            program: formData.programme,
            city: formData.city,
            universityName: value,
          });
        }
        break;

      default:
        break;
    }
  };

  const fetchCalculations = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/student/calculation/all`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response?.data?.success) {
        setCards(response.data.data);
      }
    } catch (error) {
      // console.error(error?.response?.data?.message || "Error fetching calculations");
    } finally {
      setIsLoading(false);
    }
  }


  const saveCalculation = async () => {
    try {
      setIsLoading(true);
      const response = await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/student/calculation/save`,
        {
          country: formData.country,
          city: formData.city,
          programme: formData.programme,
          university: formData.university,
          major: formData.major,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response?.data?.success) {
        setStep(1);
        setFormData({
          country: "",
          city: "",
          programme: "",
          university: "",
          major: "",
        });
        fetchCalculations();
      }

    } catch (error) {
      // console.error(error?.response?.data?.message || "Error saving calculation");
    } finally {
      setIsLoading(false);
    }
  }

  useGSAP(() => {
    // if (cards.length > 0) {
    //   const firstCard = document.getElementById(cards[0].id); // Animate the first card (newest)
    //   if (firstCard) {
    //     gsap.fromTo(
    //       firstCard,
    //       { opacity: 0, x: -50 },
    //       { opacity: 1, x: 0, duration: 1.5, ease: "power2.out" }
    //     );
    //   }
    // }
    // Select all card elements inside the container
    const cardElements = document.querySelectorAll("#cards-container > div");
    if (cardElements.length > 0) {
      gsap.fromTo(
        cardElements,
        { opacity: 0, x: -50 },
        { opacity: 1, x: 0, duration: 1, ease: "power4.in", stagger: 0.2 }
      );
    }

  }, [cards]);

  // Handle focus event
  const handleFocus = (field) => {
    setIsFocused((prev) => ({ ...prev, [field]: true }));
  };

  // Handle blur event: mark the field as not focused
  const handleBlur = (field) => {
    setIsFocused((prev) => ({ ...prev, [field]: false }));
  };

  // Helper to check if a field actually has a value
  const fieldHasValue = (fieldValue) => {
    if (Array.isArray(fieldValue)) {
      return fieldValue.length > 0;
    }
    return !!fieldValue;
  };

  // Determine label styles based on field state
  const getLabelStyles = (field, value) => {
    const hasVal = fieldHasValue(value);

    if (isFocused[field]) {
      return {
        color: "#22c55e", // green when focused
        top: "-10px",
        fontSize: "12px",
        zIndex: 20,
      };
    } else if (hasVal) {
      return {
        color: "#6b7280", // gray when not focused, but value exists
        top: "-10px",
        fontSize: "12px",
        zIndex: 20,
      };
    } else {
      return {
        color: "#6b7280", // gray default when empty
        top: "8px",
        fontSize: "14px",
        zIndex: 20,
      };
    }
  };


  // Determine border color based on field state
  const getBorderColor = (field) => {
    if (isFocused[field]) {
      return "#22c55e"; // green
    } else {
      return "#6b7280"; // gray
    }
  };

  // Common react-select styles
  const commonSelectStyles = (field) => ({
    placeholder: "", // remove default "Select..."
    styles: {
      control: (base, state) => ({
        ...base,
        minHeight: "42px",
        borderColor: getBorderColor(field),
        "&:hover": {
          borderColor: getBorderColor(field),
        },
        "&:focus-within": {
          borderColor: "#22c55e",
          boxShadow: "0 0 0 #22c55e",
        },
        backgroundColor: state.isDisabled ? "white" : base.backgroundColor,
        zIndex: 10,
      }),
      menu: (base) => ({
        ...base,
        zIndex: 30,
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? "white"
          : state.isFocused
            ? "#3265CB"
            : base.backgroundColor,
        color: state.isSelected
          ? "#374151"
          : state.isFocused
            ? "white"
            : base.color,
        "&:active": {
          backgroundColor: "#3265CB",
          color: "white",
        },
      }),
      indicatorSeparator: () => ({
        display: "none",
      }),
    },
  });

  // Check if step 1 is complete
  const isStep1Complete = formData.country && formData.city && formData.programme;

  // Handle next/submit button click
  const handleNext = () => {
    if (step === 1) {
      setStep(2);
    } else {
      // handleSubmit();
      saveCalculation();
    }
  };

  // Handle form submission
  const handleSubmit = () => {
    const newCard = {
      id: new Date().toISOString(),
      ...formData,
    };

    setCards((prev) => {
      const updatedCards = [newCard, ...prev]; // Add new card to the beginning
      return updatedCards.length > 3 ? updatedCards.slice(0, 3) : updatedCards; // Keep only 3 cards
    });

    setStep(1);
    setFormData({
      country: "",
      city: "",
      programme: "",
      university: "",
      major: "",
    });
  };

  // Render step 1 fields
  const renderStep1 = () => (
    <>
      <div className="relative">
        <span
          style={getLabelStyles("country", formData.country)}
          className="absolute text-sm text-gray-500 transform left-3 bg-white px-1 transition-all duration-200"
        >
          Country *
        </span>
        <Select
          id="country"
          name="country"
          options={countries.map((country) => ({
            value: getDisplayValue(country, ""),
            label: getDisplayValue(country, ""),
          }))}
          value={
            formData.country
              ? {
                  value: getDisplayValue(formData.country, ""),
                  label: getDisplayValue(formData.country, ""),
                }
              : null
          }
          onChange={(selectedOption) => handleChange("country", selectedOption)}
          onFocus={() => handleFocus("country")}
          onBlur={() => handleBlur("country")}
          {...commonSelectStyles("country")}
        />
        <span className="text-xs mt-1 text-gray-500">
          Please select Country
        </span>
      </div>

      <div className="relative">
        <span
          style={getLabelStyles("programme", formData.programme)}
          className="absolute text-sm text-gray-500 transform left-3 bg-white px-1 transition-all duration-200"
        >
          Programme *
        </span>
        <Select
          id="programme"
          name="programme"
          options={[
            { value: "Masters", label: "Masters" },
            { value: "Bachelors", label: "Bachelors" },
          ]}
          value={
            formData.programme
              ? { value: formData.programme, label: formData.programme }
              : null
          }
          onChange={(selectedOption) => handleChange("programme", selectedOption)}
          onFocus={() => handleFocus("programme")}
          onBlur={() => handleBlur("programme")}
          isDisabled={!formData.country}
          {...commonSelectStyles("programme")}
        />
        <span className="text-xs mt-1 text-gray-500">
          Please select Programme
        </span>
      </div>

      <div className="relative">
        <span
          style={getLabelStyles("city", formData.city)}
          className="absolute text-sm text-gray-500 transform left-3 bg-white px-1 transition-all duration-200"
        >
          City *
        </span>
        <Select
          id="city"
          name="city"
          options={cities.map((city) => ({
            value: getDisplayValue(city, ""),
            label: getDisplayValue(city, ""),
          }))}
          value={
            formData.city
              ? {
                  value: getDisplayValue(formData.city, ""),
                  label: getDisplayValue(formData.city, ""),
                }
              : null
          }
          onChange={(selectedOption) => handleChange("city", selectedOption)}
          onFocus={() => handleFocus("city")}
          onBlur={() => handleBlur("city")}
          isDisabled={!formData.programme || !formData.country}
          {...commonSelectStyles("city")}
        />
        <span className="text-xs mt-1 text-gray-500">
          Please select City
        </span>
      </div>
    </>
  );

  // Render step 2 fields
  const renderStep2 = () => (
    <>
      <div className="relative">
        <span
          style={getLabelStyles("university", formData.university)}
          className="absolute text-sm text-gray-500 transform left-3 bg-white px-1 transition-all duration-200"
        >
          University *
        </span>
        <Select
          id="university"
          name="university"
          options={universities.map((university) => ({
            value: getDisplayValue(university, ""),
            label: getDisplayValue(university, ""),
          }))}
          value={
            formData.university
              ? {
                  value: getDisplayValue(formData.university, ""),
                  label: getDisplayValue(formData.university, ""),
                }
              : null
          }
          onChange={(selectedOption) => handleChange("university", selectedOption)}
          onFocus={() => handleFocus("university")}
          onBlur={() => handleBlur("university")}
          isDisabled={!formData.city}
          {...commonSelectStyles("university")}
        />
        <span className="text-xs mt-1 text-gray-500">
          Please select University
        </span>
      </div>

      <div className="relative">
        <span
          style={getLabelStyles("major", formData.major)}
          className="absolute text-sm text-gray-500 transform left-3 bg-white px-1 transition-all duration-200"
        >
          Major *
        </span>
        <Select
          id="major"
          name="major"
          options={majors.map((major) => ({
            value: getDisplayValue(major, ""),
            label: getDisplayValue(major, ""),
          }))}
          value={
            formData.major
              ? {
                  value: getDisplayValue(formData.major, ""),
                  label: getDisplayValue(formData.major, ""),
                }
              : null
          }
          onChange={(selectedOption) => handleChange("major", selectedOption)}
          onFocus={() => handleFocus("major")}
          onBlur={() => handleBlur("major")}
          isDisabled={!formData.university}
          {...commonSelectStyles("major")}
        />
        <span className="text-xs mt-1 text-gray-500">
          Please select Major
        </span>
      </div>
    </>
  );

  return (
    <div id="app" className="flex flex-col w-full">
      {isLoading && <Loader />}
      {/* Cards Container */}
      <div id="cards-container" className="grid grid-cols-3 gap-4 mb-6">
        {[...cards].slice(0, 3).map((card) => (
          <div
            key={card._id}
            id={card._id}
            // className="px-6 py-4 mt-6 rounded shadow-2xl bg-gradient-to-r from-blue-500 to-blue-700 text-white w-full"
            className="px-6 py-4 mt-6 rounded shadow-lg bg-blue-500 text-white w-full"
          >
            <span className="text-white font-semibold">{getDisplayValue(card.university)}<br /></span>
            <span className="text-white text-sm">{getDisplayValue(card.city)}, {getDisplayValue(card.country)}<br /></span>
            <span className="text-white text-sm">{getDisplayValue(card.programme)}<br /></span>
            <span className="text-white text-sm">{getDisplayValue(card.major)}<br /><br /></span>

            {/* Fee rows using grid with two columns */}
            <div className="grid grid-cols-2 items-center">
              <span className="text-white text-sm font-bold">Est. Fee:</span>
              <span className="text-white text-sm text-right">PKR {card.totalFeeInPKR}</span>
            </div>
            <div className="grid grid-cols-2 items-center mt-1">
              <span className="text-white text-sm font-bold">Min Fee:</span>
              <span className="text-white text-sm text-right">PKR {card.lowestEstimateFee}</span>
            </div>
            <div className="grid grid-cols-2 items-center mt-1">
              <span className="text-white text-sm font-bold">Max Fee:</span>
              <span className="text-white text-sm text-right">PKR {card.highestEstimateFee}</span>
            </div>
          </div>
        ))}
      </div>

      {/* <div className="flex justify-end">
        <button 
          // onClick={() => navigate("/profile")}
          className="flex items-center text-blue-500 -mt-4 mb-2 py-2 px-2 rounded-full hover:bg-blue-50 transition-colors">
          <span className="font-bold">Update Profile</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="3"
              d="M13 5l7 7-7 7"
            />
          </svg>
        </button>
        </div> */}
      {/* Main Card for Form */}
      <div id="main-card" className="px-10 py-6 bg-white rounded shadow-md transition-transform transform duration-500 ease-in-out">
        <div id="step-1" className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 mt-6">
          {step === 1 ? renderStep1() : renderStep2()}
        </div>

        {/* Button */}
        <div className="flex justify-end mt-4">
          <button
            id="next-button"
            onClick={handleNext}
            disabled={step === 1 ? !isStep1Complete : !formData.university || !formData.major}
            className={`text-white border px-6 py-2 rounded bg-blue-500 hover:bg-blue-600 hover:text-white ${(step === 1 && !isStep1Complete) || (step === 2 && (!formData.university || !formData.major))
              ? "opacity-50 cursor-not-allowed"
              : ""
              }`}
          >
            {step === 1 ? "Next" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormWithCards;
